import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { requestAvatarMessage } from "@/lib/api";
import { getWeatherSnapshot } from "@/lib/weatherClient";

const CELEBRATION_MOODS = new Set(["party", "hype", "proud", "excited"]);
// Moods that get a *gentle* weather tilt. Other states (sad, sleepy,
// frustrated, determined, plus the celebration moods above) are left alone
// so the world outside never steamrolls a stronger emotion.
const TILTABLE_BASE_MOODS = new Set(["happy", "cozy", "worried"]);
const SUMMON_LINES = [
    "look at you 🍡 — proud-mode activated.",
    "if I had hands I'd be clapping.",
    "yes!! you. you specifically. 🍡",
    "rice ball salute for you.",
    "okay confetti time. you earned this.",
];

// Apply a small weather-flavored tilt to the baseline mood.
//   rainy / snowy + (happy|worried|cozy) → cozy   (curl-up weather)
//   sunny + happy                         → excited (a tiny brightness bump)
// Returns the original mood untouched in every other case.
function applyWeatherTilt(base, snap) {
    if (!base || !snap || !TILTABLE_BASE_MOODS.has(base)) return base;
    const c = snap.condition;
    if (c === "rainy" || c === "snowy") return "cozy";
    if (c === "sunny" && base === "happy") return "excited";
    return base;
}

// Owns everything related to Mochi's speech bubble + mood layering:
//   - bubbleMsg / bubbleLoading state
//   - forcedMood (easter eggs, summon celebrations, absence return)
//   - tap-to-mute celebrations (so a celebration doesn't sit indefinitely)
//   - displayMood derivation from { baseMood, nervousness, forcedMood, muted }
//   - celebrate() / idleMessage() / summonCelebration() / handleAvatarTap()
//
//   const mood = useMochiMood({ baseMood, nervousness, stats });
//   <SpeechBubble message={mood.bubbleMsg} loading={mood.bubbleLoading} />
export function useMochiMood({ baseMood, nervousness, stats }) {
    const [bubbleMsg, setBubbleMsg] = useState("Hi! I'm Mochi 🌸");
    const [bubbleLoading, setBubbleLoading] = useState(false);
    const [forcedMood, setForcedMood] = useState(null);
    const [mutedCelebrationKey, setMutedCelebrationKey] = useState(null);

    // Read the current cached weather snapshot (set after user opts in via
    // the Morning Check-In sky-peek). Used to nudge the baseline so Mochi's
    // vibe quietly drifts with the world outside.
    const weatherSnap = getWeatherSnapshot();
    const tiltedBase = applyWeatherTilt(baseMood, weatherSnap);

    // Celebration-mute key — changes whenever a fresh win lands so the next
    // celebration fires regardless of prior mutes.
    const celebrationKey = `${baseMood}|${stats?.tasks_done ?? 0}-${stats?.habits_done ?? 0}-${stats?.reminders_done ?? 0}`;
    const isMutedCelebration = mutedCelebrationKey === celebrationKey && CELEBRATION_MOODS.has(baseMood);

    const displayMood = forcedMood
        ? forcedMood
        : nervousness >= 2 ? "frustrated"
        : nervousness >= 1 ? "worried"
        : isMutedCelebration ? "happy"
        : tiltedBase;

    // Avatar reaction on task/habit completion
    const celebrate = useCallback(async (event, ctx) => {
        setBubbleLoading(true);
        try {
            const snap = getWeatherSnapshot();
            const enriched = snap ? { ...(ctx || {}), weather: snap.condition, season: snap.season } : ctx;
            const r = await requestAvatarMessage(event, "proud", enriched);
            setBubbleMsg(r.message);
        } catch {
            setBubbleMsg("Yesss! So proud of you 💖");
        }
        setBubbleLoading(false);
        toast.success(event === "task_completed" ? "Task done!" : "Streak vibes!");
    }, []);

    // Idle bubble messages (avatar tap, after morning check-in)
    const idleMessage = useCallback(async () => {
        setBubbleLoading(true);
        try {
            const snap = getWeatherSnapshot();
            const ctx = stats ? {
                tasks_done: stats.tasks_done, tasks_total: stats.tasks_total,
                habits_done: stats.habits_done, habits_total: stats.habits_total,
            } : {};
            if (snap) {
                ctx.weather = snap.condition;
                ctx.season = snap.season;
            }
            const event = stats && stats.completion_pct >= 1
                && (stats.tasks_total + stats.habits_total + stats.reminders_total) > 0
                ? "all_done" : "idle";
            const r = await requestAvatarMessage(event, baseMood, ctx);
            setBubbleMsg(r.message);
        } catch {
            setBubbleMsg("I'm here whenever you need me 🌿");
        }
        setBubbleLoading(false);
    }, [baseMood, stats]);

    // Tap-driven: mute current celebration + show idle message
    const handleAvatarTap = useCallback(() => {
        if (CELEBRATION_MOODS.has(displayMood) && !forcedMood) {
            setMutedCelebrationKey(celebrationKey);
        }
        idleMessage();
    }, [displayMood, forcedMood, celebrationKey, idleMessage]);

    // Summon a 6-second `party` celebration on demand (chat trigger / streak pill)
    const summonCelebration = useCallback(() => {
        setForcedMood("party");
        setMutedCelebrationKey(null);
        const line = SUMMON_LINES[Math.floor(Math.random() * SUMMON_LINES.length)];
        setBubbleMsg(line);
        setBubbleLoading(false);
        toast("🎉 🍡 🎉", { duration: 3000, style: { fontSize: "22px", textAlign: "center" } });
        setTimeout(() => setForcedMood((m) => (m === "party" ? null : m)), 6000);
    }, []);

    // Easter-egg style 6s forced mood (used for "I love Mochi" chat trigger)
    const forceMoodFor = useCallback((moodName, ms = 6000) => {
        setForcedMood(moodName);
        setTimeout(() => setForcedMood((m) => (m === moodName ? null : m)), ms);
    }, []);

    // Brief "nom" reaction — fires when the user feeds Mochi a rice ball.
    // Mochi flashes "excited" for ~900ms then settles back to baseMood.
    const nomReact = useCallback(() => {
        setForcedMood("excited");
        setTimeout(() => setForcedMood((m) => (m === "excited" ? null : m)), 900);
    }, []);

    return useMemo(() => ({
        bubbleMsg, setBubbleMsg,
        bubbleLoading, setBubbleLoading,
        displayMood,
        celebrate,
        idleMessage,
        handleAvatarTap,
        summonCelebration,
        forceMoodFor,
        nomReact,
    }), [
        bubbleMsg, bubbleLoading, displayMood,
        celebrate, idleMessage, handleAvatarTap,
        summonCelebration, forceMoodFor, nomReact,
    ]);
}
